import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  ArrowRight, Sparkles, Compass, FileText, Map, Palette, Cpu, Check, Building2,
  MessageCircle, ScrollText, Layers, Shield, Send,
} from "lucide-react";
import FAQ from "@/components/FAQ";
import { combineJsonLd, websiteJsonLd, organizationJsonLd, faqJsonLd } from "@/lib/structuredData";

const FEATURES = [
  { icon: MessageCircle, title: "AI-rådgivare", text: "Ställ frågor om bygglov, PBL & BBR och få svar på sekunder.", to: "/chat" },
  { icon: Compass, title: "Behöver jag bygglov?", text: "AI bedömer: bygglov, anmälan, Attefall eller lovbefriat.", to: "/guide/permit" },
  { icon: Map, title: "Fastighetsanalys", text: "Detaljplan, byggrätter, prickmark, höjder och servitut.", to: "/guide/property" },
  { icon: FileText, title: "Handlingsmallar", text: "Färdiga mallar: kontrollplan, situationsplan, ansökan.", to: "/guide/templates" },
  { icon: Layers, title: "Ritningsgranskning", text: "Ladda upp ritningar och få AI-feedback mot BBR/PBL.", to: "/guide/review" },
  { icon: Shield, title: "Anmäl tillsyn", text: "Misstänkt olovligt bygge? Anmäl direkt till kommunen.", to: "/anmal-tillsyn" },
];

const STEPS = [
  { n: "01", title: "Idé", text: "Ställ en fråga eller välj vad du vill bygga." },
  { n: "02", title: "Bedömning", text: "AI:n säger om du behöver bygglov, anmälan eller inget." },
  { n: "03", title: "Analys", text: "Vi hittar detaljplan, byggrätter och förutsättningar för din fastighet." },
  { n: "04", title: "Handlingar", text: "Skapa ritningar, ladda upp egna eller använd våra mallar." },
  { n: "05", title: "Granskning", text: "AI granskar projektet mot BBR/PBL innan du skickar in." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const jsonLd = combineJsonLd(websiteJsonLd, organizationJsonLd, faqJsonLd);
  return (
    <>
      <Seo
        title="Gratis hjälp med bygglovsansökan"
        description="Kostnadsfri AI-rådgivning, regelmotor enligt PBL/BBR och färdig ansökan-PDF att skicka till kommunen. Bygglovskompassen sparar tid och undviker vanliga misstag."
        path="/"
        keywords="bygglov, bygglovsansökan, PBL, BBR, attefall, friggebod, carport bygglov, gratis bygglovshjälp"
        jsonLd={jsonLd}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-zinc-50">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute right-0 top-0 w-1/2 h-full opacity-10 hidden lg:block">
          <img src="https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold uppercase tracking-wider rounded-sm">
              <Sparkles className="w-3 h-3 stroke-[2]" /> Drivs av Claude Sonnet 4.5 + Gemini 3 Pro
            </div>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-zinc-900 leading-[1.05]"
              style={{ fontFamily: "Outfit" }}>
              Från idé<br />till färdigt<br />bygglov.
            </h1>
            <p className="mt-6 text-lg text-zinc-600 max-w-xl leading-relaxed">
              Den AI-drivna plattformen för dig som vill bygga. Vi guidar dig genom regler, ritningar och handlingar – så att du kan fokusera på drömprojektet.
            </p>
            <div className="mt-8 max-w-xl">
              <form
                onSubmit={(e) => { e.preventDefault(); if (question.trim()) navigate(`/chat?q=${encodeURIComponent(question.trim())}`); }}
                className="flex items-stretch gap-2 bg-white border border-zinc-300 rounded-sm p-1.5 shadow-sm focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-100 transition"
                data-testid="hero-question-form"
              >
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Skriv din fråga om bygglov…"
                  className="flex-1 min-w-0 px-3 py-2.5 text-base text-zinc-900 placeholder:text-zinc-400 bg-transparent outline-none"
                  data-testid="hero-question-input"
                />
                <button
                  type="submit"
                  disabled={!question.trim()}
                  className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white px-4 sm:px-5 py-2.5 rounded-sm font-medium text-sm active:scale-[0.98] transition"
                  data-testid="hero-question-submit"
                >
                  <span className="hidden sm:inline">Fråga gratis</span> <Send className="w-4 h-4" />
                </button>
              </form>
              <div className="mt-2 flex flex-wrap gap-2">
                {["Behöver jag bygglov för altan?", "Attefallshus 30 kvm – vad gäller?"].map((s) => (
                  <button key={s} type="button" onClick={() => navigate(`/chat?q=${encodeURIComponent(s)}`)}
                    className="text-xs px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-100 rounded-sm transition"
                    data-testid="hero-suggestion-chip">
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Link to="/guide/permit" data-testid="hero-cta-permit"
                className="inline-flex items-center gap-2 bg-white hover:bg-zinc-50 text-zinc-900 border border-zinc-300 px-6 py-3.5 rounded-sm font-medium text-base">
                Behöver jag bygglov?
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-6 text-xs text-zinc-500">
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Helt gratis för medborgare</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> 290 kommuner täcks</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Ingen inloggning krävs</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Vad du kan göra</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
              Allt du behöver inför ditt bygglov.
            </h2>
            <p className="mt-3 text-zinc-600">Sex AI-drivna verktyg som tar dig hela vägen från frågetecken till färdig ansökan.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <Link
                key={f.title}
                to={f.to}
                data-testid={`feature-${i}`}
                className="group relative bg-white border border-zinc-200 rounded-sm p-6 transition-all duration-200 hover:border-blue-400 hover:-translate-y-[1px]"
              >
                <f.icon className="w-7 h-7 text-blue-700 stroke-[1.5]" />
                <h3 className="mt-5 text-xl font-medium text-zinc-900 group-hover:text-blue-700 transition" style={{ fontFamily: "Outfit" }}>
                  {f.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{f.text}</p>
                <ArrowRight className="absolute right-5 bottom-5 w-4 h-4 text-zinc-300 group-hover:text-blue-700 transition" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-zinc-50 py-20 lg:py-28 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Så fungerar det</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
              Fem steg från frågetecken till färdig ansökan.
            </h2>
          </div>
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200">
            {STEPS.map((s) => (
              <li key={s.n} className="bg-white p-6 lg:p-8">
                <div className="text-xs font-semibold tracking-[0.2em] text-blue-700">{s.n}</div>
                <h3 className="mt-3 text-lg font-medium text-zinc-900" style={{ fontFamily: "Outfit" }}>{s.title}</h3>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-zinc-50 py-20 lg:py-28 border-t border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">FAQ</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
              Vanliga frågor.
            </h2>
          </div>
          <div className="mt-12">
            <FAQ />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Cpu className="w-10 h-10 mx-auto stroke-[1.5] opacity-90" />
          <h2 className="mt-6 text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Kom igång på under en minut.
          </h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto">
            Ställ din första fråga gratis – ingen registrering krävs.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/chat" data-testid="cta-chat-bottom"
              className="inline-flex items-center gap-2 bg-white text-blue-800 hover:bg-zinc-100 px-6 py-3.5 rounded-sm font-medium">
              Starta AI-rådgivare <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/register" data-testid="cta-register-bottom"
              className="inline-flex items-center gap-2 border border-white/40 hover:bg-white/10 text-white px-6 py-3.5 rounded-sm font-medium">
              Skapa konto
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
